import { useState, type FormEvent } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import { nodeProjectIds, type NodeCapacityRow, type NodePresence, type NodePressureRow, type OwnerRuleRow, type Project, type SessionRef, type Worker } from '../lib/data';
import { useApprovalPolicyMutation, useNodeEnrollment, useNodeInstruction, useNodeLimits, useNodeProject } from '../lib/mutations';
import { checkLimits, stateForNode, RESERVE_MAX_PERCENT, RESERVE_MIN_PERCENT } from '../lib/capacity';
import { computerReachability, connectionState, type OperationalStatus } from '../lib/presence';
import { agentBrief, hideServiceAddress, installCommands, windowsInstallerFile, NODE_HOST_KINDS, type NodeHostKind } from '../lib/install';
import { OwnerContractControl } from './OwnerContractControl';
import { RuntimeSetup } from './RuntimeSetup';
import { RemoveComputer } from './RemoveComputer';

/**
 * Connected / disconnected state of every node, whether each one has room for
 * more work, and the projects it works on. A computer the engine is refusing
 * to give work to used to look exactly like one sitting idle; now it says so.
 */
export function ConnectionsPanel({ client, guard, orgId, nodes, projects, sessions, capacity, pressure, nowMs, onChanged }: {
  client: SupabaseClient; guard: Guard; orgId: string; nodes: readonly NodePresence[]; projects: readonly Project[];
  sessions: readonly SessionRef[]; capacity: readonly NodeCapacityRow[]; pressure: readonly NodePressureRow[];
  nowMs: number; onChanged: () => Promise<void>;
}) {
  const { setProject, busy, error } = useNodeProject(client);
  const { setLimits, busy: limitsBusy, error: limitsError } = useNodeLimits(client);
  const { instruct, busy: instructionBusy, error: instructionError } = useNodeInstruction(client);
  // The node + project whose folder is being edited before switching it on (or changing it).
  const [editing, setEditing] = useState<{ nodeId: string; projectId: string; folder: string; seats: string } | null>(null);
  // The computer whose reserves are being changed, and what has been typed so far.
  const [reserves, setReserves] = useState<{ nodeId: string; memory: string; graphics: string } | null>(null);
  const [rejected, setRejected] = useState<string | null>(null);
  if (nodes.length === 0) return <p className="muted">No nodes connected yet.</p>;
  // Assigning work needs the same verified owner session as approval decisions.
  const save = (nodeId: string, projectId: string, enabled: boolean, folder?: string | null, seats?: number) => void guard('post_owner_command', async () => {
    if (await setProject(orgId, nodeId, projectId, enabled, folder, seats)) { setEditing(null); await onChanged(); }
  });
  // Reserves are checked here before anything is sent, and the database checks
  // them again; whichever refuses, the owner is told rather than left guessing.
  const saveReserves = (form: { nodeId: string; memory: string; graphics: string }) => {
    const checked = checkLimits({ memory: form.memory, graphics: form.graphics });
    if (!checked.ok) { setRejected(checked.message); return; }
    setRejected(null);
    void guard('post_owner_command', async () => {
      if (await setLimits(orgId, form.nodeId, checked.value)) { setReserves(null); await onChanged(); }
    });
  };
  const provision = (nodeId: string) => void guard('post_owner_command', async () => {
    if (await instruct(orgId, nodeId, 'provision')) await onChanged();
  });
  const rescanAis = (nodeId: string) => void guard('post_owner_command', async () => {
    if (await instruct(orgId, nodeId, 'rescan_runtimes')) await onChanged();
  });
  return (
    <div className="stack">
      <ul className="list">
        {nodes.map((n) => {
          const state = connectionState({ lastSeenAt: n.last_seen_at, status: n.status as OperationalStatus, nowMs });
          const working = nodeProjectIds(n.node_id, sessions);
          const room = stateForNode(n.node_id, capacity, pressure, nowMs);
          // A computer nothing is known about keeps the row it has always had:
          // empty scaffolding would say less than saying nothing.
          const known = room.readiness !== 'not_reported';
          return (
            <li key={n.node_id} className="stack">
              <div className="row spread">
                <span>
                  <span className={`dot dot-${state}`} aria-hidden="true" /> {n.name}
                  {known && (
                    <>
                      {' · '}
                      <span className={`dot dot-${room.tone}`} aria-hidden="true" />
                      <span className="muted">{room.phrase}</span>
                    </>
                  )}
                </span>
                <span className="muted">{computerReachability({ status: n.status, lastSeenAt: n.last_seen_at, nowMs }).line}</span>
              </div>
              {known && <small className="muted">{room.line}</small>}
              {/* What the computer last said is free on the volume its run checkouts live on.
                  Shown whenever it has said, ready or not: a disk nobody was shown is how
                  one filled up unnoticed. */}
              {room.diskFree !== null && (
                <small className={room.diskLow ? undefined : 'muted'} data-disk-free={n.node_id}>
                  Disk: {room.diskFree}{room.diskLow ? ' - low, so no new work goes here until space is freed' : ''}
                </small>
              )}
              {projects.length > 0 && (
                <div className="row" role="group" aria-label={`Projects ${n.name} works on`}>
                  <small className="muted">Works on:</small>
                  {projects.map((p) => {
                    const on = working.has(p.id);
                    return (
                      <span key={p.id} className="row">
                        <button type="button" className={on ? 'chip on' : 'chip'} aria-pressed={on} disabled={busy}
                          title={on ? `Stop sending ${p.name} work to ${n.name}` : `Send ${p.name} work to ${n.name}`}
                          onClick={() => (on ? save(n.node_id, p.id, false) : setEditing({ nodeId: n.node_id, projectId: p.id, folder: '', seats: '' }))}>
                          {on ? '✓ ' : '+ '}{p.name}
                        </button>
                        {on && (
                          <button type="button" className="link" title="Change the folder it works in" aria-label={`Folder for ${p.name} on ${n.name}`}
                            onClick={() => setEditing({ nodeId: n.node_id, projectId: p.id, folder: '', seats: '' })}>
                            📁
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}
              {editing?.nodeId === n.node_id && (
                <form className="row" onSubmit={(event) => {
                  event.preventDefault();
                  const seats = /^[1-8]$/.test(editing.seats.trim()) ? Number(editing.seats.trim()) : undefined;
                  save(editing.nodeId, editing.projectId, true, editing.folder.trim() || undefined, seats);
                }}>
                  <input aria-label="Project folder on this computer" maxLength={400} value={editing.folder}
                    placeholder={`Folder on ${n.name}, e.g. C:\\Users\\me\\GitHub\\site (empty: keep the current folder)`}
                    onChange={(event) => setEditing({ ...editing, folder: event.target.value })} />
                  <input aria-label="Worker seats on this computer" inputMode="numeric" maxLength={1} size={14} value={editing.seats}
                    placeholder="worker seats 1-8 (empty: keep)"
                    onChange={(event) => setEditing({ ...editing, seats: event.target.value })} />
                  <button type="submit" className="primary" disabled={busy}>Start</button>
                  <button type="button" className="link" onClick={() => setEditing(null)}>Cancel</button>
                </form>
              )}
              <div className="row">
                <button type="button" disabled={instructionBusy || state !== 'connected'}
                  title={state === 'connected' ? `Install or repair the approved AI workforce on ${n.name}` : `${n.name} must be connected first`}
                  onClick={() => provision(n.node_id)}>
                  Provision AI workforce
                </button>
                <button type="button" disabled={instructionBusy || state !== 'connected'}
                  title={state === 'connected'
                    ? `Re-detect approved AI tools already installed on ${n.name}`
                    : `${n.name} must be connected first`}
                  onClick={() => rescanAis(n.node_id)}>
                  Rescan AIs
                </button>
                <button type="button" className="link" title={`How much of ${n.name} stays free for you`}
                  onClick={() => { setRejected(null); setReserves(reserves?.nodeId === n.node_id ? null : { nodeId: n.node_id, memory: '', graphics: '' }); }}>
                  {reserves?.nodeId === n.node_id ? 'Cancel' : 'Keep some of this computer free'}
                </button>
              </div>
              {reserves?.nodeId === n.node_id && (
                <form className="row" onSubmit={(event) => { event.preventDefault(); saveReserves(reserves); }}>
                  <input aria-label={`Memory to keep free on ${n.name}`} inputMode="numeric" maxLength={2} size={16} value={reserves.memory}
                    placeholder={`memory ${RESERVE_MIN_PERCENT}-${RESERVE_MAX_PERCENT}% (empty: keep)`}
                    onChange={(event) => setReserves({ ...reserves, memory: event.target.value })} />
                  <input aria-label={`Video memory to keep free on ${n.name}`} inputMode="numeric" maxLength={2} size={16} value={reserves.graphics}
                    placeholder={`video memory ${RESERVE_MIN_PERCENT}-${RESERVE_MAX_PERCENT}% (empty: keep)`}
                    onChange={(event) => setReserves({ ...reserves, graphics: event.target.value })} />
                  <button type="submit" className="primary" disabled={limitsBusy}>Save</button>
                  <small className="muted">No worker starts here while less than this is free.</small>
                </form>
              )}
              <RemoveComputer client={client} guard={guard} orgId={orgId} nodeId={n.node_id} nodeName={n.name} onChanged={onChanged} />
            </li>
          );
        })}
      </ul>
      {error && <p role="alert">{error}</p>}
      {rejected && <p role="alert">{rejected}</p>}
      {limitsError && <p role="alert">{limitsError}</p>}
      {instructionError && <p role="alert">{instructionError}</p>}
    </div>
  );
}

/** Add a computer as a node: a one-time code, valid for 30 minutes, that the node claims. */
export function AddNode({ client, guard, orgId, connection }: { client: SupabaseClient; guard: Guard; orgId: string; connection: { url: string; publishableKey: string } }) {
  const { create, busy, error } = useNodeEnrollment(client);
  const [name, setName] = useState('');
  const [issued, setIssued] = useState<{ code: string; expiresAt: string; name: string } | null>(null);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    const nodeName = name.trim();
    // Creating a code needs the same verified owner session as approval decisions.
    void guard('post_owner_command', async () => {
      const value = await create(orgId, nodeName);
      if (value) { setIssued({ ...value, name: nodeName }); setName(''); }
    });
  };
  return (
    <div className="stack">
      <RuntimeSetup />
      <form onSubmit={onSubmit} className="row">
        <input aria-label="New node name" placeholder="computer name, e.g. MacBook" maxLength={64} pattern="[A-Za-z0-9 ._\-]+" value={name} onChange={(e) => setName(e.target.value)} required />
        <button type="submit" disabled={busy}>Add node</button>
      </form>
      {error && <p role="alert">{error}</p>}
      {issued && <InstallCommands issued={issued} connection={connection} onDone={() => setIssued(null)} />}
      <SetupVideo />
    </div>
  );
}

/** Owner rules: the permanent owner rules and whether a drafted plan waits for Go ahead. */
export function RulesPanel({ client, guard, orgId, ownerRules = [], planApproval = 'auto', onSaved }: {
  client: SupabaseClient; guard: Guard; orgId: string;
  /** The permanent owner rules, exactly as stated. Shown first; nothing here can change one. */
  ownerRules?: readonly OwnerRuleRow[];
  planApproval?: 'auto' | 'ask';
  onSaved: () => Promise<void>;
}) {
  const policy = useApprovalPolicyMutation(client);
  const setPlanApproval = (next: 'auto' | 'ask') => void guard('post_owner_command', async () => {
    if (await policy.savePlanApproval(orgId, next)) await onSaved();
  });

  return (
    <div className="stack">
      {ownerRules.length > 0 && (
        <section className="stack" aria-label="Permanent owner rules">
          <strong>Permanent owner rules</strong>
          <ul className="list">
            {ownerRules.map((rule) => (
              <li key={rule.rule_key} data-owner-rule={rule.rule_key}>
                <span className="tag">{rule.permanent ? 'permanent' : 'owner rule'}</span>{' '}
                {rule.statement}
                <span className="muted"> · stated {rule.stated_on}</span>
              </li>
            ))}
          </ul>
          <small className="muted">Your standing rules for Brunel itself. They are kept in the owner contract (AGENTS.md) and cannot be turned off or deleted here.</small>
        </section>
      )}
      <OwnerContractControl client={client} guard={guard} orgId={orgId} />
      <fieldset className="stack">
        <legend>Plan approval</legend>
        <label className="row">
          <input aria-label="Plan approval" type="checkbox" checked={planApproval === 'ask'} disabled={policy.busy}
            onChange={(e) => setPlanApproval(e.target.checked ? 'ask' : 'auto')} />
          <span>{planApproval === 'ask' ? 'ON' : 'OFF'}</span>
        </label>
        <small className="muted">{planApproval === 'ask'
          ? 'Every plan drafted from your command waits for Go ahead before any work starts.'
          : 'Your own commands are the approval: their work starts, or is queued for a busy or offline Worker, without asking again.'}</small>
        {policy.error && <p role="alert">{policy.error}</p>}
      </fieldset>
    </div>
  );
}

function InstallCommands({ issued, connection, onDone }: {
  issued: { code: string; expiresAt: string; name: string }; connection: { url: string; publishableKey: string }; onDone: () => void;
}) {
  const [os, setOs] = useState<'windows' | 'mac'>(() => (navigator.userAgent.includes('Windows') ? 'windows' : 'mac'));
  const [copied, setCopied] = useState(false);
  const [showCommand, setShowCommand] = useState(false);
  const [showBrief, setShowBrief] = useState(false);
  const [briefCopied, setBriefCopied] = useState(false);
  // The OS tab answers most of it; a NAS or a model machine is the part it cannot.
  const [hostKind, setHostKind] = useState<NodeHostKind>(() => (navigator.userAgent.includes('Windows') ? 'windows' : 'macos'));
  const commands = installCommands(window.location.origin, issued.code, connection, issued.name);
  const command = commands[os];
  const visibleCommand = hideServiceAddress(command, connection.url);
  const copy = async () => { await navigator.clipboard.writeText(command); setCopied(true); window.setTimeout(() => setCopied(false), 2000); };
  const download = () => {
    const file = windowsInstallerFile(window.location.origin, issued.code, connection, issued.name);
    const href = URL.createObjectURL(new Blob([file.content], { type: 'application/octet-stream' }));
    const link = document.createElement('a');
    link.href = href; link.download = file.fileName;
    document.body.append(link); link.click(); link.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 10_000);
  };
  const until = new Date(issued.expiresAt).toLocaleTimeString();
  const brief = agentBrief(window.location.origin, issued.code, connection, issued.name, hostKind, issued.expiresAt);
  const visibleBrief = hideServiceAddress(brief, connection.url);
  const copyBrief = async () => { await navigator.clipboard.writeText(brief); setBriefCopied(true); window.setTimeout(() => setBriefCopied(false), 2000); };
  return (
    <div className="card stack install">
      <strong>Install on {issued.name}</strong>
      <div className="row segmented" role="tablist" aria-label="Operating system">
        <button type="button" role="tab" aria-selected={os === 'windows'} className={os === 'windows' ? 'active' : ''} onClick={() => setOs('windows')}>Windows</button>
        <button type="button" role="tab" aria-selected={os === 'mac'} className={os === 'mac' ? 'active' : ''} onClick={() => setOs('mac')}>Mac / Linux</button>
      </div>
      {os === 'windows' && (
        <>
          <small className="muted">
            Download the installer on {issued.name} (or copy it there) and double-click it. If Windows says it protected your PC, choose More info, then Run anyway.
          </small>
          <div className="row">
            <button type="button" className="primary" onClick={download}>Download installer</button>
            <button type="button" className="link" onClick={() => setShowCommand((v) => !v)}>{showCommand ? 'Hide command' : 'Use a command instead'}</button>
            <button type="button" className="link" onClick={onDone}>Done</button>
          </div>
        </>
      )}
      {(os === 'mac' || showCommand) && (
        <>
          <small className="muted">
            {os === 'windows' ? 'On that computer open PowerShell and paste:' : 'On that computer open Terminal and paste:'}
          </small>
          <code className="block">{visibleCommand}</code>
          <div className="row">
            <button type="button" className={os === 'mac' ? 'primary' : ''} onClick={() => void copy()}>{copied ? 'Copied' : 'Copy command'}</button>
            {os === 'mac' && <button type="button" className="link" onClick={onDone}>Done</button>}
          </div>
        </>
      )}
      <small className="muted">
        It installs what it needs (Node.js on Windows if missing), sets the computer up with every AI tool and model it finds, connects with code <code>{issued.code}</code> (works once, until {until}) and keeps running in the background. Nothing to answer.
      </small>
      <div className="row">
        <button type="button" className="link" onClick={() => setShowBrief((v) => !v)}>
          {showBrief ? 'Hide the brief for an AI agent' : 'Let an AI agent do it instead'}
        </button>
      </div>
      {showBrief && (
        <>
          <small className="muted">
            Nobody has to follow steps: paste this into an AI agent running on that computer and it will carry the install through. Tell it what kind of machine it is:
          </small>
          <label className="row">
            <span>Machine kind</span>
            <select aria-label="Machine kind" value={hostKind} onChange={(e) => setHostKind(e.target.value as NodeHostKind)}>
              {NODE_HOST_KINDS.map((kind) => <option key={kind} value={kind}>{kind.replace('_', ' ')}</option>)}
            </select>
          </label>
          <code className="block">{visibleBrief}</code>
          <div className="row">
            <button type="button" onClick={() => void copyBrief()}>{briefCopied ? 'Copied' : 'Copy brief'}</button>
          </div>
          <small className="muted">
            It carries the one-time code, so treat it like the code: send it to that computer and nowhere else.
          </small>
        </>
      )}
    </div>
  );
}

/** Short silent walk-through of adding a computer, for the owner or anyone they send it to. */
function SetupVideo() {
  const [os, setOs] = useState<'windows' | 'mac'>(() => (navigator.userAgent.includes('Windows') ? 'windows' : 'mac'));
  return (
    <details className="setup-video">
      <summary>How to add a computer (1-minute video)</summary>
      <div className="row segmented" role="tablist" aria-label="Video for">
        <button type="button" role="tab" aria-selected={os === 'windows'} className={os === 'windows' ? 'active' : ''} onClick={() => setOs('windows')}>Windows</button>
        <button type="button" role="tab" aria-selected={os === 'mac'} className={os === 'mac' ? 'active' : ''} onClick={() => setOs('mac')}>Mac / Linux</button>
      </div>
      <video key={os} controls preload="none" playsInline poster={`/help/add-node-${os}.jpg`} src={`/help/add-node-${os}.mp4`}>
        <a href={`/help/add-node-${os}.mp4`}>Open the video</a>
      </video>
      <small className="muted">Send this link to whoever sets up the computer: <code>{`${window.location.origin}/help/add-node-${os}.mp4`}</code></small>
    </details>
  );
}
